import {
  DomainFault,
  type DomainModule,
  type DomainReadContext,
} from '../../contracts';
import type { Entity, Role } from '../../types';
import { capabilities } from '../device';
import { durationOf } from '../recording';
import type {
  FenceEvent,
  FenceState,
  FenceVersion,
  Point,
  PositionSample,
  TrajectoryGap,
} from './types';

export * from './types';

function fail(s: string): never {
  throw new DomainFault('VALIDATION', s);
}
const cross = (a: Point, b: Point, c: Point) =>
  (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
const on = (a: Point, b: Point, p: Point) =>
  Math.abs(cross(a, b, p)) < 1e-10 &&
  p[0] >= Math.min(a[0], b[0]) - 1e-10 &&
  p[0] <= Math.max(a[0], b[0]) + 1e-10 &&
  p[1] >= Math.min(a[1], b[1]) - 1e-10 &&
  p[1] <= Math.max(a[1], b[1]) + 1e-10;
export function validatePolygon(points: Point[]) {
  if (
    !Array.isArray(points) ||
    points.length < 3 ||
    points.length > 100 ||
    points.some(
      (p) =>
        !Array.isArray(p) ||
        p.length !== 2 ||
        p.some((n) => !Number.isFinite(n) || n < 0 || n > 1),
    )
  )
    fail('至少三个有效点，坐标范围0–1');
  if (new Set(points.map((p) => p.join(','))).size !== points.length)
    fail('顶点不能重复');
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i],
      b = points[(i + 1) % points.length];
    area += a[0] * b[1] - b[0] * a[1];
    for (let j = i + 1; j < points.length; j++) {
      if (j === i + 1 || (i === 0 && j === points.length - 1)) continue;
      const c = points[j],
        d = points[(j + 1) % points.length];
      if (
        (cross(a, b, c) * cross(a, b, d) < 0 &&
          cross(c, d, a) * cross(c, d, b) < 0) ||
        on(a, b, c) ||
        on(a, b, d) ||
        on(c, d, a) ||
        on(c, d, b)
      )
        fail('围栏不能自交或重叠');
    }
  }
  if (Math.abs(area) < 1e-8) fail('围栏面积不能为零');
  return points;
}
export function classify(
  point: Point,
  accuracy: number,
  fence: Pick<FenceVersion, 'points' | 'accuracyMeters'>,
): 'inside' | 'outside' | 'uncertain' {
  if (
    !Number.isFinite(accuracy) ||
    accuracy < 0 ||
    accuracy > fence.accuracyMeters
  )
    return 'uncertain';
  let inside = false,
    distance = Infinity;
  // Schematic extent is explicitly 1000 × 1000 simulated metres.
  for (
    let i = 0, j = fence.points.length - 1;
    i < fence.points.length;
    j = i++
  ) {
    const a = fence.points[j],
      b = fence.points[i];
    const dx = b[0] - a[0],
      dy = b[1] - a[1];
    const t = Math.max(
      0,
      Math.min(
        1,
        ((point[0] - a[0]) * dx + (point[1] - a[1]) * dy) / (dx * dx + dy * dy),
      ),
    );
    distance = Math.min(
      distance,
      Math.hypot(point[0] - a[0] - t * dx, point[1] - a[1] - t * dy) * 1000,
    );
    if (
      a[1] > point[1] !== b[1] > point[1] &&
      point[0] < ((b[0] - a[0]) * (point[1] - a[1])) / (b[1] - a[1]) + a[0]
    )
      inside = !inside;
  }
  return distance <= accuracy ? 'uncertain' : inside ? 'inside' : 'outside';
}
export function locationCapability(ctx: DomainReadContext, device: Entity) {
  const model = ctx.list('model').find((m) => m.title === device.model);
  return {
    supported: capabilities(model).定位 === '已验证',
    version:
      model?.fields?.publishedVersion || `legacy:${model?.uuid || 'unknown'}:1`,
  };
}
export function samples(
  ctx: DomainReadContext,
  deviceUuid: string,
): PositionSample[] {
  const device = ctx.get(deviceUuid);
  if (device?.kind !== 'device') return [];
  const capability = locationCapability(ctx, device);
  if (!capability.supported) return [];
  return ctx
    .list('position')
    .filter((r) => r.deviceUuid === deviceUuid)
    .flatMap((r) => {
      const point: Point = [Number(r.fields?.x), Number(r.fields?.y)],
        accuracy = Number(r.fields?.accuracy);
      if (
        point.some((n) => !Number.isFinite(n) || n < 0 || n > 1) ||
        !Number.isFinite(accuracy) ||
        accuracy < 0 ||
        !Number.isFinite(Date.parse(r.createdAt))
      )
        return [];
      return [
        {
          uuid: r.uuid,
          deviceUuid,
          campus: r.campus,
          at: r.createdAt,
          point,
          accuracyMeters: accuracy,
          source: r.fields?.method || '来源未记录',
          revision: r.revision,
          capabilityVersion:
            r.fields?.capabilityVersion || '历史能力版本未记录',
        },
      ];
    })
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}
export function gaps(
  points: PositionSample[],
  from: string,
  to: string,
  thresholdMs = 15 * 60000,
): TrajectoryGap[] {
  const result: TrajectoryGap[] = [];
  let cursor = Date.parse(from);
  const end = Date.parse(to);
  if (!Number.isFinite(cursor) || !Number.isFinite(end) || end <= cursor)
    return result;
  const within = points
    .filter((p) => Date.parse(p.at) >= cursor && Date.parse(p.at) < end)
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  if (!within.length) return [{ from, to, reason: '所选时间没有位置采样' }];
  if (Date.parse(within[0].at) > cursor)
    result.push({ from, to: within[0].at, reason: '首次采样前没有位置证据' });
  cursor = Date.parse(within[0].at);
  for (const p of within) {
    const time = Date.parse(p.at);
    if (time - cursor > thresholdMs)
      result.push({
        from: new Date(cursor + thresholdMs).toISOString(),
        to: p.at,
        reason: '采样间隔超过15分钟，无可用连续位置证据',
      });
    cursor = time;
  }
  if (end - cursor > thresholdMs)
    result.push({
      from: new Date(cursor + thresholdMs).toISOString(),
      to,
      reason: '此后没有采样',
    });
  return result;
}
export function pointAt(points: PositionSample[], at: string) {
  const t = Date.parse(at);
  const last = points.filter((p) => Date.parse(p.at) <= t).at(-1);
  return last && t - Date.parse(last.at) <= 15 * 60000 ? last : undefined;
}
export function recordingOffset(
  recording: Entity,
  at: string,
): number | undefined {
  if (!recording.startAt) return undefined;
  const ms = Date.parse(at) - Date.parse(recording.startAt);
  return ms >= 0 && ms < durationOf(recording) ? ms : undefined;
}
export const fenceOf = (row: Entity): FenceState =>
  JSON.parse(row.fields?.fenceState || 'null') || {
    draft: { points: [], accuracyMeters: 50 },
    versions: [],
  };
export const eventsOf = (row: Entity): FenceEvent[] =>
  JSON.parse(row.fields?.fenceEvents || '[]');
export function fencePreview(
  ctx: DomainReadContext,
  points: Point[],
  accuracyMeters: number,
  campus = ctx.context.campus,
) {
  validatePolygon(points);
  if (
    !Number.isFinite(accuracyMeters) ||
    accuracyMeters < 1 ||
    accuracyMeters > 500
  )
    fail('精度阈值范围1–500米');
  return ctx
    .list('device')
    .filter((d) => campus === '全部校区' || d.campus === campus)
    .flatMap((d) => samples(ctx, d.uuid))
    .map((p) => ({
      sampleUuid: p.uuid,
      at: p.at,
      outcome: classify(p.point, p.accuracyMeters, { points, accuracyMeters }),
      revision: p.revision,
    }));
}
export const fenceFingerprint = (
  points: Point[],
  accuracyMeters: number,
  preview: ReturnType<typeof fencePreview>,
) => JSON.stringify({ points, accuracyMeters, preview });
const roles: Role[] = [
  'deviceManager',
  'campusManager',
  'groupOperator',
  'regionManager',
];
export const locationDomain: DomainModule = {
  namespace: 'location',
  resource: 'location',
  version: 1,
  migrations: [
    {
      version: 1,
      up(s) {
        for (const row of s.entities.filter((r) => r.kind === 'zone'))
          if (!row.fields?.fenceState) {
            let points: Point[] = [];
            try {
              points = validatePolygon(JSON.parse(row.fields?.points || '[]'));
            } catch {}
            const draft = { points, accuracyMeters: 50 };
            const version: FenceVersion = {
              ...draft,
              uuid: `legacy:${row.uuid}:1`,
              version: 1,
              publishedAt: row.updatedAt,
              enabled: true,
            };
            row.fields = {
              ...row.fields,
              fenceState: JSON.stringify({
                draft,
                versions:
                  points.length && row.status === 'published' ? [version] : [],
                activeUuid:
                  points.length && row.status === 'published'
                    ? version.uuid
                    : undefined,
              }),
            };
          }
      },
    },
  ],
  commands: {
    report: {
      roles,
      kind: 'position',
      execute(ctx, input) {
        const d = ctx.get(input.uuid);
        if (d?.kind !== 'device' || !locationCapability(ctx, d).supported)
          fail('设备定位能力未验证');
        const x = Number(input.fields?.x),
          y = Number(input.fields?.y),
          accuracy = Number(input.fields?.accuracy);
        if (
          [x, y].some((n) => !Number.isFinite(n) || n < 0 || n > 1) ||
          !Number.isFinite(accuracy) ||
          accuracy < 1 ||
          accuracy > 500
        )
          fail('坐标或精度无效');
        const previous = samples(ctx, d.uuid).at(-1);
        const row = ctx.append('position', `${d.title}模拟位置`, {
          campus: d.campus,
          deviceUuid: d.uuid,
          status: 'ready',
          fields: {
            x: String(x),
            y: String(y),
            accuracy: String(accuracy),
            method: '模拟WiFi定位',
            capabilityVersion: locationCapability(ctx, d).version,
          },
        });
        const events: FenceEvent[] = [];
        for (const zone of ctx
          .list('zone')
          .filter((z) => z.campus === d.campus && z.status === 'published')) {
          const f = fenceOf(zone);
          const v = f.versions.find((v) => v.uuid === f.activeUuid);
          if (!v?.enabled) continue;
          let outcome: FenceEvent['outcome'] = classify([x, y], accuracy, v);
          if (
            previous &&
            Date.parse(ctx.now) - Date.parse(previous.at) <= 15 * 60000
          ) {
            const before = classify(previous.point, previous.accuracyMeters, v);
            if (before === 'outside' && outcome === 'inside') outcome = 'enter';
            if (before === 'inside' && outcome === 'outside') outcome = 'leave';
          }
          events.push({
            uuid: crypto.randomUUID(),
            zoneUuid: zone.uuid,
            version: structuredClone(v),
            at: ctx.now,
            outcome,
            previousSampleUuid: previous?.uuid,
          });
        }
        row.fields = { ...row.fields, fenceEvents: JSON.stringify(events) };
        return row;
      },
    },
    confirm: {
      roles,
      kind: 'position',
      execute(ctx, input) {
        const row = ctx.get(input.uuid);
        if (row?.kind !== 'position' || !input.note?.trim())
          fail('请填写人工核实说明');
        const events = eventsOf(row);
        const event = events.find((e) => e.uuid === input.fields?.eventUuid);
        if (input.fields?.eventUuid && !event) fail('事件不存在');
        if (event)
          event.confirmation = {
            actor: ctx.identity.memberUuid,
            at: ctx.now,
            conclusion: input.note,
          };
        row.note = input.note;
        row.fields = {
          ...row.fields,
          fenceEvents: JSON.stringify(events),
          confirmedBy: ctx.identity.memberUuid,
        };
        ctx.touch(row);
        return row;
      },
    },
    saveFence: {
      roles,
      kind: 'zone',
      execute(ctx, input) {
        let points: Point[];
        try {
          points = JSON.parse(input.fields?.points || '');
        } catch {
          return fail('坐标格式无效');
        }
        validatePolygon(points);
        const accuracyMeters = Number(input.fields?.accuracyMeters);
        const campus = input.uuid
          ? ctx.get(input.uuid)?.campus
          : input.fields?.campus || ctx.context.campus;
        if (
          !campus ||
          campus === '全部校区' ||
          (ctx.context.campus !== '全部校区' &&
            campus !== ctx.context.campus) ||
          !ctx.list('device').some((d) => d.campus === campus)
        )
          fail('请选择授权校区');
        fencePreview(ctx, points, accuracyMeters, campus);
        if (!input.title?.trim()) fail('请填写区域名称');
        const row = input.uuid
          ? ctx.get(input.uuid)
          : ctx.append('zone', input.title, {
              status: 'draft',
              campus,
            });
        if (row?.kind !== 'zone') fail('区域不可访问');
        const f = fenceOf(row);
        f.draft = { points, accuracyMeters };
        row.title = input.title;
        row.fields = { ...row.fields, fenceState: JSON.stringify(f) };
        ctx.touch(row);
        return row;
      },
    },
    publishFence: {
      roles,
      kind: 'zone',
      execute(ctx, input) {
        const row = ctx.get(input.uuid);
        if (row?.kind !== 'zone') fail('区域不存在');
        const f = fenceOf(row);
        const prior = input.fields?.restoreUuid
          ? f.versions.find((v) => v.uuid === input.fields?.restoreUuid)
          : undefined;
        if (input.fields?.restoreUuid && !prior) fail('历史版本不存在');
        const draft = prior || f.draft;
        const preview = fencePreview(
          ctx,
          draft.points,
          draft.accuracyMeters,
          row.campus,
        );
        if (!preview.length) fail('没有授权样本，需补充位置采样后再发布');
        if (
          Math.abs(Date.now() - Number(input.fields?.previewAt)) > 900000 ||
          !Number.isFinite(Number(input.fields?.previewAt)) ||
          input.fields?.fingerprint !==
            fenceFingerprint(draft.points, draft.accuracyMeters, preview)
        )
          throw new DomainFault('CONFLICT', '预览失效，请重新测试样本');
        const v: FenceVersion = {
          ...draft,
          uuid: crypto.randomUUID(),
          version: f.versions.length + 1,
          publishedAt: ctx.now,
          enabled: input.fields?.enabled !== 'false',
          restoredFrom: prior?.uuid,
        };
        f.versions.push(v);
        f.activeUuid = v.uuid;
        row.status = v.enabled ? 'published' : 'disabled';
        row.fields = { ...row.fields, fenceState: JSON.stringify(f) };
        ctx.touch(row);
        return row;
      },
    },
  },
  query(ctx) {
    return {
      devices: ctx.list('device').map((d) => ({
        deviceUuid: d.uuid,
        capability: locationCapability(ctx, d),
        samples: samples(ctx, d.uuid),
      })),
    };
  },
};
